/**
 * AmbientField — the soft atmosphere image behind the breathing visual.
 *
 * Renders the generated per-category background full-bleed, with a
 * gentle radial vignette so the center stays uniform underneath the orb.
 * Crossfades softly when the theme or category changes.
 *
 * No interactive content here — `aria-hidden="true"` throughout.
 */
import { useEffect, useState } from 'react';

import './AmbientField.css';

/**
 * Produce a CSS `image-set()` value that prefers AVIF, then WebP, then
 * falls back to the canonical PNG. Browsers that don't understand
 * image-set() ignore the rule entirely; we set the PNG via the second
 * `background-image` declaration as the universal fallback.
 */
function imageSetFor(pngPath: string): string {
  const avif = pngPath.replace(/\.png$/, '.avif');
  const webp = pngPath.replace(/\.png$/, '.webp');
  return `image-set(url("${avif}") type("image/avif"), url("${webp}") type("image/webp"), url("${pngPath}") type("image/png"))`;
}

interface AmbientFieldProps {
  /** Public URL of the atmosphere image. */
  src: string;
  /** Two-tone vignette colors as CSS values — usually variant `--qh-paper`. */
  vignetteColor: string;
}

export function AmbientField({ src, vignetteColor }: AmbientFieldProps) {
  // Soft crossfade when src changes — show the old image fading to the
  // new one rather than a hard cut.
  const [current, setCurrent] = useState(src);
  const [next, setNext] = useState<string | null>(null);

  useEffect(() => {
    if (src === current) return;
    setNext(src);
    const id = window.setTimeout(() => {
      setCurrent(src);
      setNext(null);
    }, 1600);
    return () => window.clearTimeout(id);
  }, [src, current]);

  return (
    <div className="qh-ambient" aria-hidden="true">
      <div
        className="qh-ambient__layer"
        style={{ backgroundImage: imageSetFor(current) }}
      />
      {next && (
        <div
          className="qh-ambient__layer qh-ambient__layer--next"
          style={{ backgroundImage: imageSetFor(next) }}
        />
      )}
      <div
        className="qh-ambient__vignette"
        style={
          {
            '--qh-vignette': vignetteColor,
          } as React.CSSProperties
        }
      />
    </div>
  );
}
